import { useAccountAddonList, useHostingAccount } from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import { mergeDependentQueryStates } from '~/utils/reactQuery/helpers';

import { UseLlmAddonResult } from '../utils/types';

export const LLM_ADDON_TYPES = ['llm'];

const useLlmAddon = (): UseLlmAddonResult => {
  const { id: hostingId } = useAccount();
  const hostingAccountQuery = useHostingAccount(hostingId ?? '', {
    query: { enabled: !!hostingId },
  });

  const {
    data: hostingAccountData,
    isPending: isHostingAccountPending,
    isLoading: isHostingAccountLoading,
    isSuccess: isHostingAccountSuccess,
    isError: isHostingAccountError,
    isFetching: isHostingAccountFetching,
    refetch: refetchHostingAccount,
  } = hostingAccountQuery;

  // Jarvis billing account_id aka HAL Tenant Back Ref
  const tenantBackRef = hostingAccountData?.data?.account_id ?? undefined;

  const addonListQuery = useAccountAddonList(
    tenantBackRef ?? '',
    { type: LLM_ADDON_TYPES },
    {
      query: {
        enabled: !!tenantBackRef,
        select: (response) => response?.data?.addons?.[0],
      },
    },
  );

  const { data: addonData, refetch: refetchAddon } = addonListQuery;

  const addonId = addonData?.id?.toString();
  const { isPending, isLoading, isFetching, isError } =
    mergeDependentQueryStates(
      {
        isPending: isHostingAccountPending,
        isLoading: isHostingAccountLoading,
        isSuccess: isHostingAccountSuccess,
        isFetching: isHostingAccountFetching,
        isError: isHostingAccountError,
        isChildEnabled: !!tenantBackRef,
      },
      addonListQuery,
    );

  const isAddonChainSuccess =
    isHostingAccountSuccess && (!tenantBackRef || addonListQuery.isSuccess);

  return {
    addonId,
    hostingId,
    tenantBackRef,
    hasLlmAddon: Boolean(addonId),
    isPending: !!hostingId && isPending,
    isLoading: !!hostingId && isLoading,
    isSuccess: isAddonChainSuccess,
    isError,
    isFetching,
    refetch: async (options) => {
      await refetchHostingAccount(options);
      return refetchAddon(options);
    },
  };
};

export default useLlmAddon;
